import { inject, Injectable, signal, WritableSignal, computed, Signal } from '@angular/core';
import { PermissionsClientService } from './permissions-client.service';
import { mergeUserDataLevels } from '../../shared/utils/unwrap-user-data';

// Define types for better readability and type safety.
interface LegalEntity {
  legalEntityNum: string;
  legalEntityName: string;
}

interface UserPermissions {
  role?: string;
  // Name as stored on the {org}-Users record. May be absent for SSO users whose
  // SAML assertion didn't carry given_name/family_name — that's what the
  // first-login name prompt fills in (see SecureHomeComponent).
  firstName?: string;
  lastName?: string;
  // Whether an admin has activated this account. Missing/false → the user is
  // authenticated but not yet allowed into the app (see auth.guard activation gate).
  activated?: boolean;
  legalEntities?: (LegalEntity | string)[];
  contractTypes?: string[];
  departments?: string[];
  sites?: { siteNum: string }[];
  databaseOrganizations?: string[];
}

/**
 * A dedicated store for managing the current user's permissions state.
 *
 * This service is a singleton that holds permissions data using a Signal, acting as a single
 * source of truth for the entire application. Other services and components can inject this
 * store to reactively access the latest permissions data without making direct API calls.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsStoreService {
  private permissionsClient = inject(PermissionsClientService);

  // Private signal to hold the permissions data. It is initialized to null.
  private _permissions = signal<UserPermissions | null>(null);

  // Public, read-only signal that components and services can consume.
  // This prevents outside code from directly modifying the state.
  public readonly permissions: Signal<UserPermissions | null> = this._permissions.asReadonly();

  // Activation state. `_activated` is derived from the loaded record with strict
  // equality (record-missing OR activated≠true → false). `_activationLoaded`
  // flips once the per-session fetch has completed (success or failure) so the
  // auth guard knows the value is settled. `_loadInFlight` dedupes concurrent
  // callers (guard + SecureHome) onto a single network request.
  private _activated = signal<boolean>(false);
  private _activationLoaded = signal<boolean>(false);
  private _loadInFlight: Promise<void> | null = null;

  public readonly isActivated: Signal<boolean> = this._activated.asReadonly();
  public readonly activationLoaded: Signal<boolean> = this._activationLoaded.asReadonly();

  // A computed signal to easily check for a viewer role.
  // A user is considered a viewer if their role is explicitly set to 'Viewer'.
  // Users with the Viewer role cannot create or edit entities.
  public readonly isViewer: Signal<boolean> = computed(() => {
    const currentPermissions = this._permissions();
    return currentPermissions?.role === 'Viewer';
  });

  // Admin-tier roles. Mirrors api/hc-users/proxy-utils.js ADMIN_ROLES.
  // Matched case-insensitively; if the canonical list changes server-side,
  // update here too.
  private static readonly ADMIN_ROLES = ['system administrator', 'administrator'];

  // Break-glass bootstrap accounts: ALWAYS treated as activated + admin in the
  // FRONTEND, so the first admins can never be locked out by the activation gate
  // (chicken-and-egg). Compared lowercase/trimmed.
  // ⚠ Frontend UX only — the backend is authoritative. If the backend's
  // not-activated 403 gate is live, these accounts' hc-users calls (user list /
  // set-activation) may still 403 until their cbhi-Users rows are activated
  // server-side OR the same allowlist is mirrored in the backend.
  private static readonly ALWAYS_ACTIVE_EMAILS = [
    'healthy_contracts_1@cbhi.net',
    'healthy_contracts_2@cbhi.net',
    'david.berge@businessdataapps.com',
    'rok@businessdataapps.com',
    'aondrejryan11@gmail.com',
  ];

  private isAlwaysActive(email: string): boolean {
    return PermissionsStoreService.ALWAYS_ACTIVE_EMAILS.includes((email || '').toLowerCase().trim());
  }

  /**
   * True when the loaded role is admin-tier. Used as a UX gate for admin-only
   * pages and nav links (e.g. Proxy Assignments). NOT a security boundary —
   * the backend enforces the same check on every write and a non-admin caller
   * gets 403 regardless of what this signal says.
   */
  public readonly isAdmin: Signal<boolean> = computed(() => {
    const role = this._permissions()?.role;
    if (!role) return false;
    return PermissionsStoreService.ADMIN_ROLES.includes(role.toLowerCase().trim());
  });

  /**
   * Fetches the permissions for a given email and updates the store's state.
   * Call once per session (re-fetch on org switch).
   */
  async loadUserPermissions(email: string): Promise<void> {
    const breakGlass = this.isAlwaysActive(email);
    try {
      const payload = await this.permissionsClient.getUserPermissions(email);
      const record = this.extractUserRecord(payload);
      if (record) {
        // Break-glass accounts are forced active + admin (preserving any other
        // record fields like site/legal-entity scoping).
        // The role override only ever RAISES: it fills in a role when the backend
        // record has none, and never overwrites one the backend already set. It
        // used to hardcode 'Administrator', which silently demoted real System
        // Administrators on this list — and the approval queues gate on the exact
        // 'System Administrator' string, so those accounts lost the org-wide view.
        this._permissions.set(breakGlass ? { ...record, role: record.role || 'System Administrator', activated: true } : record);
        // Strict equality: a missing record or activated !== true means not activated.
        this._activated.set(breakGlass ? true : record.activated === true);
      } else if (breakGlass) {
        // No record at all — the true chicken-and-egg bootstrap case, so grant the
        // top tier rather than a middle one that can't reach admin-only queues.
        this._permissions.set({ role: 'System Administrator', activated: true });
        this._activated.set(true);
      } else {
        this._permissions.set(null);
        this._activated.set(false);
      }
    } catch (error) {
      console.error('PermissionsStore | Failed to fetch and load permissions: ', error);
      if (breakGlass) {
        // Even if the backend lookup fails, never lock out a break-glass account.
        this._permissions.set({ role: 'System Administrator', activated: true });
        this._activated.set(true);
      } else {
        // Set to null on error to ensure a consistent, known state.
        this._permissions.set(null);
        this._activated.set(false);
      }
    }
  }

  /**
   * Locate the user record within the hc-users /user-email response regardless of
   * wrapping depth. The record's fields can be SPLIT across levels mid-migration —
   * the cbhi backend now writes `role` at ROOT while the scope arrays (sites /
   * legalEntities / departments / contractTypes / databaseOrganizations) still live
   * nested (`userData.userData`). We therefore MERGE root / `userData` /
   * `userData.userData` so ALL fields are captured — picking a single level dropped
   * whichever fields lived at the other level, which for a scoped user would drop
   * their scope arrays and (via hasNoPermissionLimits) over-expose all contracts.
   * Returns null when no level carries `role`/`activated` (treated as not-activated,
   * e.g. a brand-new user with no record yet).
   */
  private extractUserRecord(payload: any): UserPermissions | null {
    const merged = mergeUserDataLevels<UserPermissions>(payload);
    if (!merged) return null;
    const looksLikeRecord = (merged as any).role !== undefined || (merged as any).activated !== undefined;
    return looksLikeRecord ? merged : null;
  }

  /**
   * Loads the current user's record exactly once per session. Concurrent callers
   * (the auth guard and SecureHomeComponent) await the same in-flight promise, so
   * the user record is fetched a single time. Subsequent calls return immediately
   * once `_activationLoaded` is set. Call clearPermissions() to force a re-fetch
   * (logout / org switch).
   */
  async ensureLoaded(email: string): Promise<void> {
    if (this._activationLoaded()) return;
    if (this._loadInFlight) return this._loadInFlight;
    this._loadInFlight = this.loadUserPermissions(email).finally(() => {
      this._activationLoaded.set(true);
      this._loadInFlight = null;
    });
    return this._loadInFlight;
  }

  /**
   * Clears the permissions from the store. Useful for logout or user changes.
   */
  clearPermissions(): void {
    this._permissions.set(null);
    this._activated.set(false);
    this._activationLoaded.set(false);
    this._loadInFlight = null;
  }

}
